import React, { useState } from "react";
import styled from "styled-components";
import stNdTh from "../blocks/logic/stNdTH.js";
import fot from "../fot/fot.js";
import calculator from "../fot/calculator.js";
import { Container, Section, Button } from "../components/ui";
import SEO from "../components/SEO";

const PageHeader = styled.div`
  text-align: center;
  padding: 4rem 0;
  background: linear-gradient(135deg, #fef3c7 0%, #ffffff 100%);
`;

const PageTitle = styled.h1`
  margin-bottom: 1rem;
`;

const PageDesc = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  max-width: 600px;
  margin: 0 auto;
`;

const ConverterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
`;

const CardTitle = styled.h3`
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const TodayDisplay = styled.div`
  text-align: center;
`;

const TodayDate = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #0f0f0f;
  margin-bottom: 0.5rem;
`;

const TodaySeason = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #eab308;
  margin-bottom: 0.5rem;
`;

const TodayDay = styled.div`
  color: #6b7280;
  margin-bottom: 1rem;
`;

const TodayAbbr = styled.div`
  background: #f9fafb;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-family: monospace;
  font-size: 1.125rem;
  color: #374151;
`;

const ConvertForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const Select = styled.select`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #eab308;
  }
`;

const Input = styled.input`
  width: 80px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #eab308;
  }
`;

const ResultBox = styled.div`
  background: ${({ $season }) => {
    const colors = {
      winter: '#e0f2fe',
      sprinter: '#dcfce7',
      summid: '#fef9c3',
      fautumn: '#ffedd5',
      midsea: '#f3e8ff',
    };
    return colors[$season?.toLowerCase()] || '#f9fafb';
  }};
  padding: 1.5rem;
  border-radius: 0.75rem;
  text-align: center;
  margin-top: 1rem;
`;

const ResultText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f0f0f;
`;

const SeasonExplainer = styled.div`
  margin-top: 4rem;
`;

const SeasonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SeasonCard = styled.div`
  background: ${({ $color }) => $color};
  padding: 1.5rem 1rem;
  border-radius: 0.75rem;
  text-align: center;
`;

const SeasonIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const SeasonName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const SeasonDays = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const Time = () => {
  const calLang = fot().props.children[1];
  const dayofMonth = calLang.dayNumM;
  const season = calLang.seasonNameY;
  const month = calLang.monthNameS;
  const dayOfWeek = calLang.dayNameW;
  const suffix = stNdTh(dayofMonth);
  const gregDate = new Date();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const [selectedMonth, setSelectedMonth] = useState(
    gregDate.toLocaleString("en-US", { month: "short" })
  );
  const [selectedDate, setSelectedDate] = useState(gregDate.getDate());

  const toConvert = selectedMonth.slice(0, 3) + " " + selectedDate;
  const conversion = calculator(toConvert);
  const conversionSeason = conversion.split(" ")[0];

  return (
    <>
      <SEO
        title="Time Converter"
        description="Convert any date to The New Calendar format. Find out what day it is in the 5-season system."
        url="https://thenewcalendar.com/time"
      />

      <PageHeader>
        <Container>
          <PageTitle>Time Converter</PageTitle>
          <PageDesc>
            See today's date in The New Calendar or convert any Gregorian date 
            to the 5-season format.
          </PageDesc>
        </Container>
      </PageHeader>

      <Section>
        <Container>
          <ConverterGrid>
            {/* Today's Date */}
            <Card>
              <CardTitle>Today in The New Calendar</CardTitle>
              <TodayDisplay>
                <TodayDate>
                  {gregDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </TodayDate>
                <TodaySeason>{month} {season}</TodaySeason>
                <TodayDay>
                  the {dayofMonth}{suffix} • a {dayOfWeek}
                </TodayDay>
                <TodayAbbr>
                  {calLang.monthNameS[0]}{calLang.seasonNumY + 1}.{dayofMonth}
                </TodayAbbr>
              </TodayDisplay>
            </Card>

            {/* Date Converter */}
            <Card>
              <CardTitle>Convert a Date</CardTitle>
              <ConvertForm>
                <InputGroup>
                  <Select 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {months.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </Select>
                  <Input 
                    type="number" 
                    min="1" 
                    max="31" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </InputGroup>
                <ResultBox $season={conversionSeason}>
                  <ResultText>{conversion}</ResultText>
                </ResultBox>
              </ConvertForm>
            </Card>
          </ConverterGrid>

          {/* Season Explainer */}
          <SeasonExplainer>
            <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>The Five Seasons</h2>
            <SeasonGrid>
              <SeasonCard $color="#e0f2fe">
                <SeasonIcon>❄️</SeasonIcon>
                <SeasonName>Winter</SeasonName>
                <SeasonDays>Days 1-73</SeasonDays>
              </SeasonCard>
              <SeasonCard $color="#dcfce7">
                <SeasonIcon>🌱</SeasonIcon>
                <SeasonName>Sprinter</SeasonName>
                <SeasonDays>Days 74-146</SeasonDays>
              </SeasonCard>
              <SeasonCard $color="#fef9c3">
                <SeasonIcon>☀️</SeasonIcon>
                <SeasonName>Summid</SeasonName>
                <SeasonDays>Days 147-219</SeasonDays>
              </SeasonCard>
              <SeasonCard $color="#ffedd5">
                <SeasonIcon>🍂</SeasonIcon>
                <SeasonName>Fautumn</SeasonName>
                <SeasonDays>Days 220-292</SeasonDays>
              </SeasonCard>
              <SeasonCard $color="#f3e8ff">
                <SeasonIcon>🌊</SeasonIcon>
                <SeasonName>Midsea</SeasonName>
                <SeasonDays>Days 293-365</SeasonDays>
              </SeasonCard>
            </SeasonGrid>
          </SeasonExplainer>
        </Container>
      </Section>
    </>
  );
};

export default Time;
